import React, { useState } from 'react';
import cn from 'classnames';
import AsyncSelect from 'react-select/async';
import { Option } from '~/common/models';
import { defaultStyle } from '../dropdown/default-dropdown-style';
import styles from './index.module.scss';

interface DropdownProps {
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  className?: string;
  error?: string;
  value?: Option;
  onChange?: (value: Option | null) => void;
  loadOptions?: (
    inputValue: string,
    callback: (options: Option[]) => void
  ) => void;
  onInputChange?: (inputValue: string) => void;
  defaultOptions?: boolean | Option[];
  rounded?: boolean;
  bordered?: boolean;
  dropdownIndicator?: () => JSX.Element;
  displayNoOptions?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

type Props = DropdownProps;

export const Autocomplete = React.forwardRef<AsyncSelect<Option>, Props>(
  (
    {
      id,
      name,
      label,
      placeholder,
      value,
      loadOptions,
      onInputChange,
      onChange,
      className,
      defaultOptions,
      error,
      rounded,
      bordered,
      dropdownIndicator,
      displayNoOptions,
      onKeyDown,
    },
    ref
  ) => {
    const style = defaultStyle({ rounded, bordered });
    const [options, setOptions] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState('');
    const wrappedLoadOptions = (
      input: string,
      callback: (options: any[]) => void
    ) => {
      if (loadOptions) {
        loadOptions(input, (loadedOptions: any[]) => {
          setOptions(loadedOptions || []);
          callback(loadedOptions);
        });
      } else {
        callback([]); // or provide default empty options
        setOptions([]);
      }
    };

    return (
      <div className={cn(styles.Dropdown, className)}>
        <label htmlFor={name} className={styles.Dropdown__label}>
          {label}
        </label>

        <AsyncSelect
          id={id}
          ref={ref}
          name={name}
          value={value}
          onChange={onChange}
          loadOptions={wrappedLoadOptions}
          onKeyDown={onKeyDown}
          onInputChange={(input) => {
            setInputValue(input);
            return onInputChange?.(input);
          }}
          placeholder={placeholder}
          components={{
            IndicatorSeparator: null,
            ...(!!dropdownIndicator
              ? { DropdownIndicator: dropdownIndicator }
              : {}),
          }}
          defaultOptions={defaultOptions}
          menuPortalTarget={document.body}
          styles={{
            ...style,
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          isClearable={false}
          openMenuOnClick={false}
          openMenuOnFocus
          cacheOptions
          menuIsOpen={
            displayNoOptions
              ? options.length > 0 && inputValue.length > 2
              : undefined
          }
        />
        {error && <span className={styles.Dropdown__errorMsg}>{error}</span>}
      </div>
    );
  }
);

Autocomplete.defaultProps = {
  bordered: true,
  rounded: false,
};
